import { Component, OnInit, OnDestroy, Inject, HostBinding } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';

import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';

import { Group } from '../../shared/models/group.model';
import { KioskAppointment } from '../kiosk.model';
import { Locale } from '../../shared/models/locale.model';

import { KioskService } from '../kiosk.service';

import { getLocale } from '../../main/locale/locale.reducer';
import { getAppointments, getSubscriptionId, getUserHin, getError, getTimer, isFetching, KioskError, KioskTimer } from '../kiosk.reducer';
import { ConfirmKioskAppointment, StartKioskTimer } from '../kiosk.actions';

import * as moment from 'moment';
import { orderBy } from 'lodash';

@Component({
  selector: 'kiosk-appointments',
  styles: [require('./kiosk-appointments.component.scss')],
  template: require('./kiosk-appointments.component.html'),
  preserveWhitespaces: false,
})
export class KioskAppointmentsComponent implements OnInit, OnDestroy {
  public appointments: KioskAppointment[];
  public fetchingStatus$: Observable<boolean>;
  public userConfirmed: boolean = false;
  public locale$: Observable<Locale>;

  private group: Group;
  private userHin: string;
  private subscriptionId: number;
  private destroyed: Subject<boolean> = new Subject();

  @HostBinding('class.appointments') hasAppointments: boolean = false;
  @HostBinding('class.confirmed-arrival') hasConfirmedArrivalAppointments: boolean = false;

  constructor(
    @Inject('$state') private $state,
    private kioskService: KioskService,
    private store: Store<AppState>,
  ) {};

  ngOnInit(): void {
    this.store.dispatch(new StartKioskTimer());

    this.store.select(getTimer)
      .takeUntil(this.destroyed)
      .subscribe((timer: KioskTimer): void => {
        if (timer === KioskTimer.Inactive) {
          this.goBackHome();
        }
      });

    this.locale$ = this.store.select(getLocale);

    this.fetchingStatus$ = this.store.select(isFetching);

    this.store.select(getError)
      .takeUntil(this.destroyed)
      .subscribe((error: KioskError): void => {
        if (error !== null) {
          this.$state.go('kiosk.error');
        }
      });

    this.store.select((state: AppState): Group => state.group)
      .combineLatest(
        this.store.select(getSubscriptionId),
        this.store.select(getUserHin),
        this.store.select(getAppointments),
        (group: Group, subscriptionId: number, userHin: string, appointments: KioskAppointment[]) => ({
          group,
          subscriptionId,
          userHin,
          appointments,
        }),
      )
      .takeUntil(this.destroyed)
      .subscribe(({ group, subscriptionId, userHin, appointments }): void => {
        this.group = group;
        this.subscriptionId = subscriptionId;
        this.userHin = userHin;
        this.appointments = appointments;

        this.hasAppointments = !!this.appointments.length;

        this.hasConfirmedArrivalAppointments = !!this.appointments.filter((appointment: KioskAppointment): boolean => appointment.arrived).length;

        if (!this.hasAppointments) {
          this.goBackHome();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
  }

  goBackHome(): void {
    this.$state.go('kiosk.home');
  }

  orderAppointmentsByTime(): KioskAppointment[] {
    return orderBy(this.appointments, [(appointment) => moment(appointment.startTime)]);
  }

  confirmAppointment(): void {
    const appointmentIds: number[] = this.appointments.map((appointment: KioskAppointment): number => appointment.appointmentId);

    this.store.dispatch(new StartKioskTimer());

    this.kioskService.updateArrivalStatus(this.group.id, {
      subscriptionId: this.subscriptionId,
      appointmentIds: appointmentIds,
      hin: this.userHin,
    }).subscribe((): void => {
      this.userConfirmed = true;

      appointmentIds.forEach((id: number) => {
        this.store.dispatch(new ConfirmKioskAppointment(id));
      });
    });
  }
}



// WEBPACK FOOTER //
// ../node_modules/angular2-template-loader!./ng2/kiosk/kiosk-appointments/kiosk-appointments.component.ts